/**
 * REAL LINKS (fleet ruling, Christian from Helen's feedback, 2026-10-29): every element that navigates is a
 * true `<a href>`, so right-click → Open in new tab/window, middle-click and Ctrl/⌘-click work everywhere.
 *
 * PORTABLE: this file imports no router. Each app passes its own in-app `navigate` (react-router's, or a
 * pushState for query-string apps); with none, a plain click simply follows the href. The reference copy
 * is ptc-core `web/src/components/links/`; the CRM binds it to react-router in `EntityLink.tsx`.
 *
 *  - A plain left click runs `onFollow`, then the app's `navigate(href)` (SPA, no reload).
 *  - Ctrl/⌘/Shift/Alt-click and middle-click are left to the browser (new tab/window); `onFollow` still
 *    runs for them, so side effects like "mark read" happen however the link is followed.
 *  - Keyboard: it is an anchor, so Tab reaches it and Enter follows it.
 */
import type { AnchorHTMLAttributes, ReactNode } from 'react';
import { isPlainLeftClick, realLinkProps, type RealLinkOptions } from './realLinkProps';
import './realLink.css';

export type RealLinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'onClick'> &
  RealLinkOptions & { href: string; children?: ReactNode };

/** A real link for names, cards, chips and "go to" menu items. */
export function RealLink({ href, navigate, onFollow, onClick, children, ...rest }: RealLinkProps) {
  return (
    <a {...rest} {...realLinkProps(href, { navigate, onFollow, onClick })}>
      {children}
    </a>
  );
}

/** Put this class on the row (a `<tr>` or a `role="row"` element) that holds a `RowLink`. */
export const ROW_LINK_HOST = 'row-link-host';

export type RowLinkProps = RealLinkProps & {
  /**
   * What a plain single click on the REST of the row does. `'navigate'` (default): it follows the link,
   * like clicking the name. `'double'`: only a double-click on the row follows it, and a single click on
   * the label still does (the CRM's rows, CRM-UX-13). Right-, middle- and Ctrl-click work either way.
   */
  rowClick?: 'navigate' | 'double';
};

/**
 * A real link stretched over its whole row, so right-click → Open in new tab works anywhere on the row.
 * Render it in one cell (usually the name) and put {@link ROW_LINK_HOST} on the row. Buttons, inputs and
 * other links in the row stay clickable above it (realLink.css).
 */
export function RowLink({ rowClick = 'navigate', className, onClick, onDoubleClick, children, ...rest }: RowLinkProps) {
  return (
    <RealLink
      {...rest}
      className={`row-link${className ? ` ${className}` : ''}`}
      onClick={(e) => {
        onClick?.(e);
        // The stretched area is the anchor itself (its ::after); the label is a child. `detail === 0` is a
        // keyboard Enter or a programmatic click, which always follows.
        if (rowClick === 'double' && e.target === e.currentTarget && e.detail > 0 && isPlainLeftClick(e)) {
          e.preventDefault();
        }
      }}
      onDoubleClick={(e) => {
        onDoubleClick?.(e);
        // Only the stretched area: on the label, the first click of the pair has already followed.
        if (!e.defaultPrevented && rowClick === 'double' && e.target === e.currentTarget) e.currentTarget.click();
      }}
    >
      <span className="row-link-label">{children}</span>
    </RealLink>
  );
}
